const SUBTRACT_FROM_MARGIN = "Subtract From Margin";
const DERIVED_FIELDS = new Set(["netCost", "margin", "pricingOption"]);
const { calculateMarginFromSellPrice } = require("../pricingMath");

const isBlankNumericValue = (value) =>
  value === "" || value === null || value === undefined;

const hasExplicitNumericValue = (value) =>
  !isBlankNumericValue(value) && Number.isFinite(Number(value));

const getExplicitNumericValue = (value) =>
  hasExplicitNumericValue(value) ? Number(value) : null;

const defaultToNum = (value, fallback = 0) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const isApproximatelyEqual = (left, right) =>
  Number.isFinite(left) &&
  Number.isFinite(right) &&
  Math.abs(left - right) <= 0.01;

const hasConvertedCostBasis = (row = {}) => {
  const converted = Number(row.ConvertedNetCost ?? row.convertedNetCost);
  const rate = Number(row.ExchangeRate ?? row.exchangeRate);
  return Number.isFinite(converted) && converted > 0 && Number.isFinite(rate) && rate > 0;
};

const getEffectiveDecorationCost = (row = {}, toNum = defaultToNum) => {
  if (hasConvertedCostBasis(row)) {
    return toNum(row.ConvertedNetCost ?? row.convertedNetCost, 0);
  }
  return toNum(row.NetCost ?? row.netCost, 0);
};

const deriveMarginRetail = ({ netCost = 0, margin = 0, pricingOption = "" }) => {
  if (String(pricingOption || "").trim() === SUBTRACT_FROM_MARGIN) {
    return netCost;
  }

  if (margin < 100) {
    return Number((netCost / (1 - margin / 100)).toFixed(2));
  }

  return 0;
};

const applyRetailEditToDecorationRow = ({
  row = {},
  retailValue,
  effectiveCost,
  quantity = 0,
} = {}) => {
  const retail = Number(retailValue || 0);
  const qty = Number(quantity || 0);
  const margin = calculateMarginFromSellPrice(retail, effectiveCost, row.margin);

  return {
    ...row,
    retail,
    clientPrice: retail,
    total: Number((retail * qty).toFixed(2)),
    margin,
    Margin: margin,
  };
};

const deriveRetailFromRow = ({
  row = {},
  toNum = (value, fallback = 0) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  },
  isLocked = false,
} = {}) => {
  const netCost = toNum(row.netCost, 0);
  const margin = toNum(row.margin, 0);
  const retail = getExplicitNumericValue(row.retail);
  const clientPrice = getExplicitNumericValue(row.clientPrice);
  const pricingOption = String(row.pricingOption || "").trim();

  if (isLocked) {
    if (retail !== null) return retail;
    if (clientPrice !== null) return clientPrice;
    return 0;
  }

  if (pricingOption === SUBTRACT_FROM_MARGIN) {
    return netCost;
  }

  if (retail !== null) return retail;
  if (clientPrice !== null) return clientPrice;
  return deriveMarginRetail({ netCost, margin, pricingOption });
};

const resolveRetailFromInputs = ({
  netCostValue,
  marginValue,
  pricingOptionValue,
  retailValue,
  clientPriceValue,
  changedField = "",
} = {}) => {
  const retailNum = getExplicitNumericValue(retailValue);
  const clientNum = getExplicitNumericValue(clientPriceValue);
  const fallbackRetail = deriveMarginRetail({
    netCost: netCostValue,
    margin: marginValue,
    pricingOption: pricingOptionValue,
  });
  const normalizedChangedField = String(changedField || "").trim();

  if (DERIVED_FIELDS.has(normalizedChangedField)) {
    return fallbackRetail;
  }

  if (normalizedChangedField === "clientPrice") {
    return clientNum ?? retailNum ?? fallbackRetail;
  }

  if (normalizedChangedField === "retail") {
    return retailNum ?? clientNum ?? fallbackRetail;
  }

  if (retailNum !== null) return retailNum;
  if (clientNum !== null) return clientNum;
  return fallbackRetail;
};

const calculateDefaultDecorationQuantity = (pricingRows = []) => {
  const totalFromPricing = (Array.isArray(pricingRows) ? pricingRows : []).reduce((sum, row) => {
    const qty = Number(row?.quantity || 0);
    return sum + (Number.isFinite(qty) ? qty : 0);
  }, 0);

  return totalFromPricing > 0 ? totalFromPricing : 1;
};

const isLockedBool = (value) => value === true || String(value).toLowerCase() === "true";

const isInitialAutoQuantityDecorationRow = (row = {}, rowIndex = 0) => {
  if (rowIndex !== 0) return false;
  if (isLockedBool(row?.isLocked)) return false;

  const classification = String(
    row.subType || row.SubType || row.priceType || row.chargeType || row.ChargeType || ""
  ).trim().toLowerCase();
  return classification === "run charge" || classification === "run" || row?.isRunCharge === true;
};

const recalculateDecorationRowForQuantity = (row = {}, quantity = 1) => {
  const netCost = defaultToNum(row.netCost, 0);
  const margin = defaultToNum(row.margin, 0);
  const effectiveRetail = resolveRetailFromInputs({
    netCostValue: netCost,
    marginValue: margin,
    pricingOptionValue: row.pricingOption,
    retailValue: row.retail,
    clientPriceValue: row.clientPrice,
    changedField: "quantity",
  });

  return {
    ...row,
    quantity,
    clientPrice: effectiveRetail.toFixed(2),
    total: (effectiveRetail * (parseInt(quantity, 10) || 1)).toFixed(2),
  };
};

const syncInitialDecorationRunChargeQuantities = ({
  decorations = [],
  previousPricingTotal,
  nextPricingTotal,
} = {}) => {
  if (!Array.isArray(decorations)) {
    return { decorations, changed: false };
  }

  let changed = false;
  const nextDecorations = decorations.map((decoration) => {
    if (!decoration || !Array.isArray(decoration.rows) || decoration.rows.length === 0) {
      return decoration;
    }

    const row = decoration.rows[0];
    if (!isInitialAutoQuantityDecorationRow(row, 0)) {
      return decoration;
    }

    const currentQuantity = Number(row?.quantity);
    // Explicitly classified run charges follow the current product breakdown.
    // Previously we only updated when the old charge matched the previous product
    // total, leaving copied 13-unit charges stuck when the breakdown changed.
    if (!Number.isFinite(currentQuantity)) {
      return decoration;
    }

    if (currentQuantity === nextPricingTotal) {
      return decoration;
    }

    changed = true;
    const nextRows = [...decoration.rows];
    nextRows[0] = recalculateDecorationRowForQuantity(row, nextPricingTotal);
    return {
      ...decoration,
      rows: nextRows,
    };
  });

  return { decorations: changed ? nextDecorations : decorations, changed };
};

module.exports = {
  SUBTRACT_FROM_MARGIN,
  DERIVED_FIELDS,
  isBlankNumericValue,
  hasExplicitNumericValue,
  getExplicitNumericValue,
  hasConvertedCostBasis,
  getEffectiveDecorationCost,
  deriveMarginRetail,
  calculateMarginFromSellPrice,
  applyRetailEditToDecorationRow,
  deriveRetailFromRow,
  resolveRetailFromInputs,
  calculateDefaultDecorationQuantity,
  isInitialAutoQuantityDecorationRow,
  recalculateDecorationRowForQuantity,
  syncInitialDecorationRunChargeQuantities,
};
